import type {
  BbgUsageResponse,
  HistoryResponse,
  IncidentsResponse,
  StatusResponse,
} from '@shared/contract';

/**
 * Typed API client. Every response shape comes from the shared wire contract,
 * so a field renamed on the server fails this file's typecheck rather than
 * quietly rendering `undefined` in a browser during an incident.
 */

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { Accept: 'application/json', ...init?.headers },
  });
  if (!res.ok) {
    throw new ApiError(`${path} → HTTP ${res.status}`, res.status);
  }
  return (await res.json()) as T;
}

function postJson<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
}

export const api = {
  status: () => request<StatusResponse>('/api/v1/status'),

  incidents: (limit = 150) => request<IncidentsResponse>(`/api/v1/incidents?limit=${limit}`),

  /** Batched sparkline data — one request for every check, not one per tile. */
  history: (limit = 40) => request<HistoryResponse>(`/api/v1/history?limit=${limit}`),

  ackIncident: (id: number, who = 'dashboard') =>
    postJson<{ ok: boolean; changed: boolean }>(`/api/v1/incidents/${id}/ack`, { who }),

  resolveIncident: (id: number) => postJson<{ ok: boolean }>(`/api/v1/incidents/${id}/resolve`),

  runCheck: (id: number) => postJson<{ ok: boolean }>(`/api/v1/checks/${id}/run`),

  /** Per-terminal Bloomberg usage. Errors carry the server's reason, e.g. "gateway returned HTTP 404". */
  bbgUsage: async (days = 60): Promise<BbgUsageResponse> => {
    const res = await fetch(`/api/v1/bbg-usage?days=${days}`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) {
      let reason = `HTTP ${res.status}`;
      try {
        const body = (await res.json()) as { error?: unknown };
        if (typeof body.error === 'string' && body.error) reason = body.error;
      } catch {
        // Not JSON — keep the status line.
      }
      throw new ApiError(reason, res.status);
    }
    return (await res.json()) as BbgUsageResponse;
  },
};
